import { Component } from '@angular/core';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';

/**
 * ONE COLUMN OF A BOARD: the drop list, bounded, scrolling its own cards.
 *
 * Two boards were built to this shape independently - the leads pipeline and
 * the territory patches - and what they share is not the look. It is the CDK
 * wiring, which is where both of them have already been wrong:
 *
 *  - Every column's list must be the same rectangle. CDK decides which column
 *    a drop landed in by testing the pointer against that element, so a
 *    shorter one silently refuses a card released level with a taller one's
 *    fold: the drop falls back to the source list, the handler sees its own
 *    column, and the gesture fails with no error anywhere.
 *  - The list must be registered with `cdkScrollable`, or the rectangles CDK
 *    cached when the drag started drift by the scroll offset the moment a
 *    column is scrolled mid-drag.
 *
 * Neither is visible in a screenshot. Both were found the hard way.
 *
 * WHY THE LIST IS A HOST DIRECTIVE and not an element in this template.
 *
 * The cards are the caller's, projected in. Angular resolves an injector
 * where a node is WRITTEN, not where it ends up - so a `cdkDropList` inside
 * this component's template is invisible to them: every card resolved a null
 * `dropContainer` and was undraggable while looking exactly right. That is
 * the same failure the territory board's DOM spec was written for, met from a
 * new direction, and it is why this component owns the list from its HOST,
 * which is where the caller wrote the cards.
 *
 * WHAT IS NOT SHARED is everything a person sees: the head, the cards, the
 * colours, and how the cards are spaced - the two boards space them
 * differently, one by card margins and one by a flex gap, and imposing either
 * on the other would move every card on that screen. Those are the caller's,
 * along with making its own head stick.
 *
 *   --kb-max   how tall before the cards scroll
 *   --kb-min   the empty column's height, so it stays a drop target
 *
 * The `cdkDropListGroup` stays with the caller too: leads groups a whole
 * board of several rows, territories groups one strip, and a group placed
 * here could only be one of those.
 */
@Component({
  selector: 'app-board-column',
  templateUrl: './board-column.component.html',
  styleUrls: ['./board-column.component.css'],
  hostDirectives: [
    {
      directive: CdkDropList,
      inputs: ['cdkDropListData: data'],
      outputs: ['cdkDropListDropped: dropped'],
    },
    CdkScrollable,
  ],
})
export class BoardColumnComponent {}
